import { Backdrop, Checkbox, FormGroup } from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  Col,
  Input,
  Row,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Card,
  CardBody,
  InputGroup,
  InputGroupText,
  InputGroupAddon,
} from "reactstrap";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import {
  convertCommaSeperateNumToString,
  getFutureDate,
} from "pages/CommonFunctions";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  getPlansList,
  createCustomPlan,
  getTenantCustomPlan,
} from "action/plans";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
  LIGHT_BG_COLOR,
  PLAN_DURATION_LIST,
  CURRENCY_LIST,
  PLAN_MULTIPLIER,
  CURRENCY,
  CUSTOM_STYLES,
  CURRENCY_SYMBOL,
} from "components/constants";
import { roundUpToTwoDecimals } from "pages/CommonFunctions";

function CreateCustomPlan(props) {
  const dispatch = useDispatch();
  const planListRes = useSelector((state) => state.planReducer.plansList);
  const [customPlanFields, setCustomPlanFields] = useState({
    tenantID: props.tenantId,
    paymentType: "SUBSCRIPTION", //'SUBSCRIPTION', 'ONETIME', 'OFFLINE'
    planDuration: {
      // in months
      label: "1 Month", // nn Months
      value: 1, // number
      errMsg: "",
    },
    //setting a default currency if user has been assigned any currency
    currency: {
      ...(props.currency === CURRENCY.USD
        ? { label: "$-Dollar", value: CURRENCY.USD, isDisabled: true }
        : props.currency === CURRENCY.INR
        ? { label: "₹-INR", value: CURRENCY.INR, isDisabled: true }
        : { label: "", value: "", isDisabled: false }),
      errMsg: "",
    },
    plan: {
      //selected or mapped plan name with is details on selection
      ID: 0,
      name: "",
      errMsg: "",
    },
    planMultiplier: {
      label: "plan x 1", //plan x n
      value: 1, // number
      errMsg: "",
    },
    discount: {
      value: "", //for input is string for api float
      errMsg: "",
    },
    subTotal: {
      //subtotal is base Price x multiplier - %discount
      value: "",
      errMsg: "",
    },
    linkExpiryDate: {
      value: "", //date
      errMsg: "",
    },
    isNoExpiryChecked: false, //checkbox of no expiry to payment link
    creditsGiven: {
      //this is the multiplied credits given field input
      value: 0,
      errMsg: "",
      userEnteredValue: 0,
      isUserEntered: false, //this flag taken to identify that credits are entered by user and now no need to do multiply
    },
    creditsCarryForward: {
      //this is the multiplied carry forward given field input
      value: 0,
      errMsg: "",
      userEnteredValue: 0,
      isUserEntered: false, //this flag taken to identify that carry forward credits are entered by user and now no need to do multiply
    },
    customCredits: {
      //this is the credits input field taken for personalized case
      value: "",
      errMsg: "",
    },
  });
  //states taken to show the input when clicked onm edit for credits and carry Forward credits in summary block
  const [showEditInput, setShowEditInput] = useState({
    credits: false,
    carryForward: false,
  });
  //list for select plan duration i.e 1 Months, 3 Months etc.Static list.
  const [planDurationList, setPlanDurationList] = useState(PLAN_DURATION_LIST);
  const [planList, setPlanList] = useState([]); //select plan list
  const [isLoading, setIsLoading] = useState(false); // upon create plan to show laoding

  //fo validation purpose
  const requiredFields = [
    "tenantID",
    "paymentType",
    "plan",
    "planDuration",
    "currency",
    "planMultiplier",
    "linkExpiryDate",
    "customCredits",
    "creditsGiven",
    "subTotal",
    "discount",
  ];

  // Constants for showing the plan calculation Summary
  const isUSD = customPlanFields.currency.value === CURRENCY.USD;
  // Base price
  const basePrice = isUSD
    ? customPlanFields.plan?.usdRate
    : customPlanFields.plan?.inrRate;
  // BasePrice x multiplier
  const multipliedPrice = roundUpToTwoDecimals(
    (basePrice || 0) * customPlanFields.planMultiplier.value
  );
  // In case of personalized plan
  const ratePerInvite = isUSD
    ? customPlanFields.plan.ratePerInvite
    : customPlanFields.plan.ratePerInviteInr;
  // In case of personalized Credits x rate
  const customPrice = roundUpToTwoDecimals(
    parseInt(customPlanFields.customCredits.value) * ratePerInvite
  );
  // Price to apply discount on
  const priceBeforeDiscount =
    customPlanFields.plan.ID === -1 ? customPrice : multipliedPrice;
  // Discount
  const discountGiven = roundUpToTwoDecimals(
    (priceBeforeDiscount * customPlanFields.discount.value) / 100
  );
  // GST
  const gstApplied = roundUpToTwoDecimals(
    (customPlanFields.subTotal.value * 18) / 100
  );
  // Final price with GST
  const gstAddedFinalPrice = roundUpToTwoDecimals(
    customPlanFields.subTotal.value * 1.18
  );

  useEffect(() => {
    //filters to get only Custom and Standard Plan
    const filterJSON = `?filtersJSON=[{"field": "Type","condition": "in","filterValues": ["CUSTOM","STANDARD"]}]`;
    // Fetch plans list on component mount
    getPlansList(dispatch, filterJSON);
  }, [dispatch]);

  //to set Plan List response
  useEffect(() => {
    if (planListRes && planListRes.code === 200) {
      //filter ID 1 i.e the free plan
      const filteredPlanList = planListRes.data.Plan.filter((p) => p.ID !== 1);
      setPlanList([
        ...filteredPlanList,
        //adding a Personalized Plan to create plan other than which is not available
        {
          ID: -1,
          name: "Personalized",
          isSpecial: true,
        },
      ]);
    }
  }, [planListRes]);

  //when ever anything among currency or plan or plan multiplier changes to recalculate the subtotal
  useEffect(() => {
    // if (customPlanFields.subTotal.value !== "")
    if (
      customPlanFields.currency.value &&
      customPlanFields.plan.ID &&
      customPlanFields.planMultiplier.value &&
      customPlanFields.plan.ID !== -1 //i.e not Personalized plan selected
    )
      recalculateSubtotal();
  }, [
    customPlanFields.currency.value,
    customPlanFields.plan.ID,
    customPlanFields.planMultiplier.value,
  ]);

  //the recalulation of sub total
  const recalculateSubtotal = () => {
    //get the base price of the plan based on currency
    const isUSD = customPlanFields.currency.value === CURRENCY.USD;
    //based on currency get the plan price i.e base price
    const basePrice = isUSD
      ? customPlanFields.plan?.usdRate
      : customPlanFields.plan?.inrRate;
    // BasePrice x multiplier
    const multipliedPrice = basePrice * customPlanFields.planMultiplier.value;
    //in case of personalized get rate per invite as per currency
    const ratePerInvite = isUSD
      ? customPlanFields.plan.ratePerInvite
      : customPlanFields.plan.ratePerInviteInr;
    // the subtotal is calculated as creditsEntered x ratePerInvite for personalized case only
    const customPrice =
      parseInt(customPlanFields.customCredits.value) * ratePerInvite;
    //price to apply discount on
    const priceBeforeDiscount =
      customPlanFields.plan.ID === -1 ? customPrice : multipliedPrice;

    //caculate discount by; discount = multipliedPrice * discount/100
    const discount =
      (priceBeforeDiscount * (customPlanFields.discount.value || 0)) / 100;
    //then caculate subtotal price by subtracting discount
    const subTotal = priceBeforeDiscount - discount;
    setCustomPlanFields((prevState) => ({
      ...prevState,
      subTotal: { value: roundUpToTwoDecimals(subTotal), errMsg: "" },
    }));
  };

  //On change of select planDuration
  const handlePlanDurationChange = (selected, e) => {
    // When Plan duration is 1 Month to set its state and
    // simultaneously clear the discount value and plan if personalized was selected
    // As in case of 1 Month the predefined plans are only will be shown
    if (selected.value === 1) {
      setCustomPlanFields((prevState) => {
        return {
          ...prevState,
          [e.name]: { ...selected, errMsg: "" },
          planMultiplier: {
            label: "plan x 1",
            value: 1,
            errMsg: "",
          },
          discount: {
            value: "",
            errMsg: "",
          },
          customCredits: {
            value: "",
            errMsg: "",
          },
          plan:
            customPlanFields.plan.ID === -1 //i.e if personalized plan then clear the plan input
              ? {
                  ID: 0,
                  name: "",
                  errMsg: "",
                }
              : prevState.plan,
        };
      });
    } else {
      setCustomPlanFields((prevState) => {
        return {
          ...prevState,
          [e.name]: { ...selected, errMsg: "" },
        };
      });
    }
  };

  //On change of select plan
  const handleSelectPlanChange = (selected, e) => {
    // when a -1 i.e personalized plan is selected making subtotal
    // and discount percent empty as this will be calulated in new way
    if (selected.ID === -1) {
      setCustomPlanFields((prevState) => {
        return {
          ...prevState,
          [e.name]: { ...selected, errMsg: "" },
          subTotal: {
            value: "",
            errMsg: "",
          },
          discount: {
            value: "",
            errMsg: "",
          },
        };
      });
    } else {
      //here in plan data changing the string with commas to number
      const selectedPlanData = {
        ...selected,
        creditsNum: Number(selected.credits?.replace(/,/g, "")),
        creditsCarryForwardNum: Number(
          selected.creditsCarryForward?.replace(/,/g, "")
        ),
      };
      //plan alloted credits x multiplier
      const multipliedCredits =
        selectedPlanData.creditsNum * customPlanFields.planMultiplier.value;
      //plan alloted carry forward credits x multiplier
      const multipliedCarryForward =
        selectedPlanData.creditsCarryForwardNum *
        customPlanFields.planMultiplier.value;
      setCustomPlanFields((prevState) => {
        return {
          ...prevState,
          [e.name]: {
            ...selectedPlanData,
            errMsg: "",
          },
          creditsGiven: {
            value: multipliedCredits,
            errMsg: "",
            userEnteredValue: multipliedCredits,
            isUserEntered: false, //when plan is gettign selected this flag is taken to understand the value is not beign taken form user
          },
          creditsCarryForward: {
            value: multipliedCarryForward,
            errMsg: "",
            userEnteredValue: multipliedCarryForward,
            isUserEntered: false,
          },
          customCredits: {
            value: "",
            errMsg: "",
          },
        };
      });
    }
  };

  //On change of select planMultiplier
  const handlePlanMultiplierChange = (selected, e) => {
    //plan alloted credits x multiplier
    const multipliedCredits =
      (customPlanFields.plan?.creditsNum || 0) * selected.value;
    //plan alloted carry forward credits x multiplier
    const multipliedCarryForward =
      (customPlanFields.plan?.creditsCarryForwardNum || 0) * selected.value;
    setCustomPlanFields((prevState) => {
      return {
        ...prevState,
        [e.name]: {
          ...selected,
          errMsg: "",
        },
        creditsGiven: {
          value: multipliedCredits,
          errMsg: "",
          userEnteredValue: multipliedCredits,
          isUserEntered: false, //when plan is gettign selected this flag is taken to understand the value is not beign taken form user
        },
        creditsCarryForward: {
          value: multipliedCarryForward,
          errMsg: "",
          userEnteredValue: multipliedCarryForward,
          isUserEntered: false,
        },
      };
    });
  };

  //on change of select fields
  const handleSelectChange = (selected, e) => {
    setCustomPlanFields((prevState) => {
      return {
        ...prevState,
        [e.name]: { ...selected, errMsg: "" },
      };
    });
  };

  // in case of personalized function to get the mapped plan
  // according to the entered credits changed
  const getBasePricefromCustom = (credits) => {
    // Exclude Personalized and the custom plans
    const filterPlanList = planList.filter(
      (p) => p.ID !== -1 && p.type !== "CUSTOM"
    );
    let selectedPlan = filterPlanList[0]; // Default to Starter

    // length-2 Check if credits exceed the second-last plan; if so, assign the highest plan manually
    for (let i = 0; i < filterPlanList.length - 1; i++) {
      const minCredits = parseInt(
        filterPlanList[i].credits.replace(/,/g, ""),
        10
      );
      const maxCredits = parseInt(
        filterPlanList[i + 1].credits.replace(/,/g, ""),
        10
      );

      // Calculate the midpoint of the range
      const midpoint = Math.floor((minCredits + maxCredits) / 2);

      if (credits >= midpoint) {
        selectedPlan = filterPlanList[i + 1]; // Move to the next higher plan
      } else {
        break; // Stay at the current plan
      }
    }

    // If credits exceed the highest range, apply Elite plan
    if (
      credits >=
      parseInt(
        filterPlanList[filterPlanList.length - 1].credits.replace(/,/g, ""),
        10
      )
    ) {
      selectedPlan = filterPlanList[filterPlanList.length - 1]; // Elite plan
    }

    return {
      mappedPlanID: selectedPlan.ID,
      planName: selectedPlan.name,
      usdRate: selectedPlan.usdRate,
      inrRate: selectedPlan.inrRate,
      ratePerInvite: selectedPlan.ratePerInvite,
      ratePerInviteInr: selectedPlan.ratePerInviteInr,
      creditsCarryForward: selectedPlan.creditsCarryForward,
      creditsNum: Number(selectedPlan.credits?.replace(/,/g, "")),
      creditsCarryForwardNum: Number(
        selectedPlan.creditsCarryForward?.replace(/,/g, "")
      ),
    };
  };

  //on change of Custom credits change i.e the when user will
  // entering credits and then the price will be calucluted i.e personalized case
  const handleCustomCreditsChange = (name, value) => {
    const isUSD = customPlanFields.currency.value === CURRENCY.USD;
    const mappedPlanData = getBasePricefromCustom(parseFloat(value || 0));
    const ratePerInvite = isUSD
      ? mappedPlanData.ratePerInvite
      : mappedPlanData.ratePerInviteInr;
    //Entered credits x ratePerInvite i.e price to apply discount on
    const priceBeforeDiscount = parseInt(value) * ratePerInvite;
    const discountedPrice =
      priceBeforeDiscount -
      (priceBeforeDiscount * customPlanFields.discount.value) / 100;

    setCustomPlanFields((prevState) => {
      return {
        ...prevState,
        [name]: { value, errMsg: "" },
        plan: {
          ...prevState.plan,
          ...mappedPlanData,
        },
        creditsCarryForward: {
          value: mappedPlanData.creditsCarryForwardNum,
          errMsg: "",
          userEnteredValue: mappedPlanData.creditsCarryForwardNum,
          isUserEntered: false,
        },
        subTotal: {
          value: roundUpToTwoDecimals(discountedPrice),
          errMsg: "",
        },
      };
    });
  };

  // on change of discount and subtotal interchangeably
  const handleDiscountSubtotalChange = (name, value) => {
    const isUSD = customPlanFields.currency.value === CURRENCY.USD;
    const basePrice = isUSD
      ? customPlanFields.plan?.usdRate
      : customPlanFields.plan?.inrRate;
    // multiply the base price with multiplier
    const multipliedPrice = basePrice * customPlanFields.planMultiplier.value;

    //in case of personalized
    const ratePerInvite = isUSD
      ? customPlanFields.plan.ratePerInvite
      : customPlanFields.plan.ratePerInviteInr;
    const customPrice =
      parseInt(customPlanFields.customCredits.value) * ratePerInvite;
    //price to apply discount on
    const priceBeforeDiscount =
      customPlanFields.plan.ID === -1 ? customPrice : multipliedPrice;

    setCustomPlanFields((prevState) => {
      let updatedFields = {
        ...prevState,
        [name]: { value, errMsg: "" }, //set the changed state as  it is
      };

      //make the change bidirectional i.e when discount percent changes the final prize is changed and refelected
      if (name === "discount") {
        // Calculate sub total price after discount substraction
        const discountedPrice =
          priceBeforeDiscount - (priceBeforeDiscount * value) / 100;
        const isSubTotalInvalid = roundUpToTwoDecimals(discountedPrice) <= 0;
        updatedFields.subTotal = {
          value: roundUpToTwoDecimals(discountedPrice),
          errMsg: isSubTotalInvalid
            ? "The subtotal cannot be less than or equal to 0."
            : "",
        };
        // and when subtotal price changes the discount percent is changed and refelected
      } else if (name === "subTotal") {
        // Calculate discount percentage from subtotal price
        const discount = priceBeforeDiscount
          ? ((priceBeforeDiscount - value) / priceBeforeDiscount) * 100
          : 0;
        const isInvalidDiscount =
          roundUpToTwoDecimals(discount) < 0 ||
          roundUpToTwoDecimals(discount) > 25;

        updatedFields.discount = {
          value: roundUpToTwoDecimals(discount),
          errMsg: isInvalidDiscount
            ? "The discount must be a positive value and should not exceed 25%."
            : "",
        };
      }

      return updatedFields;
    });
  };

  const handleOnBlur = (name, value) => {
    showValidationError(name, value);
  };

  const showValidationError = (name, value) => {
    const error = getValidationError(name, value);

    if (error)
      setCustomPlanFields((prevState) => {
        return {
          ...prevState,
          [name]: {
            ...prevState[name],
            errMsg: error,
          },
        };
      });
  };

  //on blur of the fields handling validations
  const getValidationError = (name, value) => {
    let errorMessage = "";
    switch (name) {
      case "duration":
        if (value === 0) {
          errorMessage = "Please select a plan duration."; //duration cannot be empty
        }
        break;
      case "currency":
        if (value === "") {
          errorMessage = "Please select a currency."; //currency cannot be empty
        }
        break;
      case "plan":
        if (value === 0) {
          errorMessage = "Please select a plan."; //plan cannot be empty
        }
        break;
      case "planMultiplier":
        if (value === 0) {
          errorMessage = "Please select plan multiplier."; //plan multiplier cannot be empty
        }
        break;
      case "discount":
        if (isNaN(value)) {
          errorMessage = "Please enter a valid discount.";
        } else if (parseFloat(value) < 0) {
          errorMessage = "Discount value must be at least 0%."; //
        } else if (parseFloat(value) > 25) {
          errorMessage = "Discount value cannot exceed 25%."; //
        }
        break;
      case "subTotal":
        if (!value || isNaN(parseFloat(value))) {
          errorMessage = "Subtotal price is required.";
        } else if (parseFloat(value) <= 0) {
          errorMessage = "Subtotal price must be greater than zero.";
        } else if (parseFloat(value) > 200000) {
          errorMessage = "Subtotal price cannot exceed 200,000.";
        }
        break;
      case "creditsGiven":
        if (customPlanFields.plan.ID !== -1) {
          //to check credits given onlywhen normal plans are selected i.e no personalized
          if (!value || isNaN(parseFloat(value))) {
            errorMessage = "Please enter credits.";
          } else if (parseFloat(value) <= 0) {
            errorMessage = "Credit value must be greater than 0.";
          } else if (parseFloat(value) > 5000000) {
            errorMessage = "Credit value cannot exceed 5,000,000.";
          }
        }
        break;
      case "creditsCarryForward":
        if (
          customPlanFields.plan.ID > 2 || //carry forward credits only for the plans above starter
          customPlanFields.plan?.mappedPlanID > 2
        ) {
          if (!value || isNaN(parseFloat(value))) {
            errorMessage = "Please enter carry forward credits.";
          } else if (parseInt(value) < 0) {
            errorMessage = "Carry forward credits value cannot be negative.";
          } else if (parseFloat(value) > 1000000) {
            errorMessage = "Carry forward credits cannot exceed 1,000,000.";
          }
        }
        break;
      case "customCredits":
        //check the custom credits field only when the personalized plan is selected
        if (customPlanFields.plan.ID === -1) {
          if (!value || isNaN(parseFloat(value))) {
            errorMessage = "Please enter credits.";
          } else if (parseFloat(value) < 500) {
            errorMessage = "Credit value must be more than 500.";
          } else if (parseFloat(value) > 5000000) {
            errorMessage = "Credit value cannot exceed 5,000,000.";
          }
        }
        break;
      case "linkExpiryDate":
        if (
          customPlanFields.paymentType !== "OFFLINE" &&
          !customPlanFields.isNoExpiryChecked &&
          value === ""
        ) {
          errorMessage = `Please select a payment link expiry date or check "No Expiry".`;
        }
        break;
      default:
        break;
    }
    return errorMessage;
    // }
  };

  //on change of date for payemnt link expiry
  const handleDateChange = (date) => {
    setCustomPlanFields((prevState) => {
      return {
        ...prevState,
        linkExpiryDate: { value: date, errMsg: "" },
      };
    });
  };

  //the summary block credit and carry forward credits change
  const handleCreditsChange = (name, value) => {
    setCustomPlanFields((prevState) => {
      return {
        ...prevState,
        [name]: {
          ...prevState[name],
          userEnteredValue: value,
          errMsg: "",
        },
      };
    });
  };

  //on selection of payment type radio button
  const handlePaymentType = (type) => {
    setCustomPlanFields((prevState) => {
      let updatedFields = { ...prevState, paymentType: type };
      //when offline is selected making th link expiry data empty
      if (type === "OFFLINE") {
        updatedFields.isNoExpiryChecked = false;
        updatedFields.linkExpiryDate = {
          value: "",
          errMsg: "",
        };
      }
      return updatedFields;
    });
  };

  //function to check the new plan duration that needs
  // to be added is valid and in form of nn Months (nn -> Number in 2 digits)
  const isDurationValid = (inputValue) => {
    //^(?:[2-9]|1[0-2]) → Matches numbers from 2 to 12.
    // " Months$" → Ensures a space followed by "Months", case-sensitive.
    const regex = /^(?:[2-9]|1[0-2]) Months$/;
    return regex.test(inputValue);
  };

  // to create a new option for plan duration
  const createOption = (label, value) => ({
    label,
    value,
  });

  // to create a new option for plan duration like 5 Months, 8 Months etc
  const handleCreateDurationOption = (inputValue) => {
    if (isDurationValid(inputValue)) {
      const monthsValue = parseInt(inputValue.slice(0, 1)); //Extract the number part
      const labelAndValue = createOption(inputValue, monthsValue);
      setPlanDurationList((prev) => [...prev, labelAndValue]);
      setCustomPlanFields((prevState) => {
        return {
          ...prevState,
          planDuration: {
            ...labelAndValue,
            errMsg: "",
          },
        };
      });
    } else {
      setCustomPlanFields((prevState) => {
        return {
          ...prevState,
          planDuration: {
            ...prevState.planDuration,
            errMsg: "Enter a valid duration in months. eg. 5 Months",
          },
        };
      });
    }
  };

  const validateForm = () => {
    let isValid = true;
    let updatedFields = { ...customPlanFields };

    requiredFields.forEach((key) => {
      if (typeof updatedFields[key] === "object") {
        const field = updatedFields[key];
        const fieldValue =
          key === "plan"
            ? field.ID
            : key === "creditsGiven" || key === "creditsCarryForward"
            ? field[
                customPlanFields[key].isUserEntered
                  ? "userEnteredValue"
                  : "value"
              ]
            : field.value;
        const errorMsg = getValidationError(key, fieldValue);
        updatedFields[key] = {
          ...field,
          errMsg: errorMsg,
        };

        if (errorMsg) isValid = false;
      } else if (
        typeof updatedFields[key] === "string" &&
        !updatedFields[key]
      ) {
        Swal.fire({
          title:
            key === "paymentType"
              ? "Please select a Payment Type"
              : "Something went wrong",
          icon: "success",
          showConfirmButton: false,
          timer: 3000,
        });
        isValid = false;
      }
    });

    // Update state with new validation errors
    setCustomPlanFields(updatedFields);

    return isValid;
  };

  const handleSubmit = async (e) => {
    if (validateForm()) {
      setIsLoading(true);
      let bodyData = {
        tenantID: customPlanFields.tenantID,
        paymentMode: customPlanFields.paymentType, //SUBSCRIPTION, ONETIME, OFFLINE
        planType:
          customPlanFields.planDuration.value === 1 //for 1 month the standard plans are picked up from the list
            ? "STANDARD"
            : customPlanFields.plan.ID === -1
            ? "PERSONALIZED"
            : "CUSTOM", //STANDARD,CUSTOM,PERSONALIZED
        planDuration: customPlanFields.planDuration.value, //in months
        currency: customPlanFields.currency.value, //INR or USD
        planID:
          customPlanFields.plan.ID === -1 //when personalized plan sending the base ID as per mapped plan according to credits entered
            ? customPlanFields.plan.mappedPlanID
            : customPlanFields.plan.ID, // in other wise the base plan id is send
        planMultiplier: customPlanFields.planMultiplier.value,
        credits:
          customPlanFields.plan.ID === -1
            ? parseInt(customPlanFields.customCredits.value)
            : parseInt(
                customPlanFields.creditsGiven[
                  customPlanFields.creditsGiven.isUserEntered
                    ? "userEnteredValue"
                    : "value"
                ]
              ),
        carryForwardCredits: parseInt(
          customPlanFields.creditsCarryForward[
            customPlanFields.creditsCarryForward.isUserEntered
              ? "userEnteredValue"
              : "value"
          ]
        ),
        planAmount: parseFloat(customPlanFields.subTotal.value), // this amount is (basePrice * Multiplier) - discount%
        discountType: "PERCENTAGE", //PERCENTAGE, FLAT
        discountValue: customPlanFields.discount.value //discount in percent
          ? parseFloat(customPlanFields.discount.value)
          : 0,
        payableAmount:
          customPlanFields.currency.value === CURRENCY.INR //in case of INR ((basePrice * Multiplier) - discount% ) + gst% //in usd (basePrice * Multiplier) - discount%
            ? parseFloat(gstAddedFinalPrice)
            : parseFloat(customPlanFields.subTotal.value),
      };

      if (customPlanFields.paymentType !== "OFFLINE") {
        bodyData = {
          ...bodyData,
          linkExpiry: customPlanFields.isNoExpiryChecked
            ? getFutureDate(2) //get future date of after 2 years
            : customPlanFields.linkExpiryDate.value.toLocaleDateString("en-CA"), // Outputs YYYY-MM-DD in local time}
        };
      }

      const res = await createCustomPlan(bodyData);
      setIsLoading(false);
      if (res && res.code === 200) {
        const querryParams = res.data.URLKey
          ? `?key=${res.data.URLKey}`
          : `?tenantID=${customPlanFields.tenantID}&status=["PENDING"]`;
        getTenantCustomPlan(dispatch, querryParams);
        props.handleClose();
        Swal.fire({
          title: `${res.message}`,
          icon: "success",
          showConfirmButton: false,
          timer: 3000,
        });
      } else {
        Swal.fire({
          title: "Failed to create custom plan.",
          text: Array.isArray(res?.data?.message)
            ? res.data.message[0]?.Msg
            : res?.data?.message
            ? res?.data?.message
            : "Something went wrong",
          icon: "error",
          showConfirmButton: false,
          timer: 3000,
        });
      }
    }
  };

  const onSubmitCreditsGiven = () => {
    if (!customPlanFields.creditsGiven.errMsg) {
      setCustomPlanFields((prevState) => {
        return {
          ...prevState,
          creditsGiven: {
            ...prevState.creditsGiven,
            isUserEntered: true,
          },
        };
      });
      setShowEditInput((prevState) => {
        return {
          ...prevState,
          credits: false,
        };
      });
    }
  };
  const onSubmitCarryForward = () => {
    if (!customPlanFields.creditsCarryForward.errMsg) {
      setCustomPlanFields((prevState) => {
        return {
          ...prevState,
          creditsCarryForward: {
            ...prevState.creditsCarryForward,
            isUserEntered: true,
          },
        };
      });
      setShowEditInput((prevState) => {
        return {
          ...prevState,
          carryForward: false,
        };
      });
    }
  };


  return (
    <>
      <Backdrop
        sx={styles.backBropStyle} // Ensures it's on top of other elements
        open={isLoading}
      >
        <i className="fa fa-spinner fa-spin fa-3x text-black" />
      </Backdrop>
      <Modal
        isOpen={props.isOpen}
        toggle={props.handleClose}
        centered
        size="xl"
        backdrop={"static"}
        scrollable={true}
        fade={true}
      >
        <>
          <ModalHeader
            toggle={() => {
              props.handleClose();
            }}
            style={{ backgroundColor: LIGHT_BG_COLOR }}
          >
            Create Custom Plan
          </ModalHeader>
          <ModalBody style={{ backgroundColor: LIGHT_BG_COLOR }}>
            <Row>
              <Col md="12">
                <FormGroup>
                  <label className="form-control-label">
                    Payment Type <strong style={{ color: "red" }}>*</strong>
                  </label>
                  {/* Payment Type Radio Inputs */}
                  <div
                    className="d-flex flex-column flex-md-row  align-items-md-center"
                    style={{ gap: "1.5rem" }}
                  >
                    {/* Type 1 - Subscription */}
                    <div
                      className="d-flex rounded bg-white p-1 py-2 px-2 border border-default"
                      style={{ gap: "0.8rem", flex: 1 }}
                    >
                      <div>
                        <input
                          type="radio"
                          id="SUBSCRIPTION"
                          name="paymentType"
                          value="SUBSCRIPTION"
                          checked={
                            customPlanFields.paymentType === "SUBSCRIPTION"
                          }
                          onChange={() => handlePaymentType("SUBSCRIPTION")}
                        />
                      </div>
                      <label
                        htmlFor="SUBSCRIPTION"
                        className="m-0 d-flex flex-column "
                      >
                        <span>Subscription-Based</span>
                        <small className="text-muted">
                          Payment Link generation
                        </small>
                      </label>
                    </div>
                    {/* Type 2 - One Type */}
                    <div
                      className="d-flex rounded bg-white p-1 py-2 px-2 border border-default"
                      style={{ gap: "0.8rem", flex: 1 }}
                    >
                      <div>
                        <input
                          type="radio"
                          id="ONETIME"
                          name="paymentType"
                          value="ONETIME"
                          checked={customPlanFields.paymentType === "ONETIME"}
                          onChange={() => handlePaymentType("ONETIME")}
                        />
                      </div>
                      <label
                        htmlFor="ONETIME"
                        className="m-0 d-flex flex-column"
                      >
                        <span>One-Time Payment</span>
                        <small className="text-muted">
                          Link generation without Subscription
                        </small>
                      </label>
                    </div>
                    {/* Type 3 - Offline */}
                    <div
                      className="d-flex rounded bg-white p-1 py-2 px-2 border border-default"
                      style={{ gap: "0.8rem", flex: 1 }}
                    >
                      <div>
                        <input
                          type="radio"
                          id="OFFLINE"
                          name="paymentType"
                          value="OFFLINE"
                          checked={customPlanFields.paymentType === "OFFLINE"}
                          onChange={() => handlePaymentType("OFFLINE")}
                        />
                      </div>
                      <label
                        htmlFor="OFFLINE"
                        className="m-0 d-flex flex-column"
                      >
                        <span>Offline</span>
                        <small className="text-muted">
                          Payment already done
                        </small>
                      </label>
                    </div>
                  </div>
                </FormGroup>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col md="4">
                <FormGroup>
                  <label className="form-control-label">
                    Plan Duration <strong style={{ color: "red" }}>*</strong>
                  </label>
                  <CreatableSelect
                    options={planDurationList}
                    placeholder="Select Plan Duration"
                    name="planDuration"
                    onChange={(selected, e) =>
                      handlePlanDurationChange(selected, e)
                    }
                    onBlur={() =>
                      handleOnBlur(
                        "planDuration",
                        customPlanFields.planDuration.value
                      )
                    }
                    styles={CUSTOM_STYLES}
                    value={planDurationList?.find(
                      (options) =>
                        options.value === customPlanFields.planDuration.value
                    )} // find the selected option based on value
                    isSearchable={true} // enable search functionality
                    className={
                      customPlanFields.planDuration.errMsg ? "is-invalid" : null
                    }
                    onCreateOption={handleCreateDurationOption}
                  />
                  {customPlanFields.planDuration.errMsg && (
                    <small className="text-danger">
                      {customPlanFields.planDuration.errMsg}
                    </small>
                  )}
                </FormGroup>
              </Col>
              <Col md="4">
                <FormGroup>
                  <label className="form-control-label">
                    Currency <strong style={{ color: "red" }}>*</strong>
                  </label>
                  <Select
                    options={CURRENCY_LIST}
                    placeholder="Select Currency"
                    name="currency"
                    // getOptionLabel={(options) => options.location}
                    // getOptionValue={(options) => options.ID}
                    onChange={(selected, e) => handleSelectChange(selected, e)}
                    onBlur={() =>
                      handleOnBlur("currency", customPlanFields.currency.value)
                    }
                    styles={CUSTOM_STYLES}
                    value={CURRENCY_LIST?.find(
                      (options) =>
                        options.value === customPlanFields.currency.value
                    )} // find the selected option based on value
                    isSearchable={true} // enable search functionality
                    className={
                      customPlanFields.currency.errMsg ? "is-invalid" : null
                    }
                    //the disabled is done only for the user who have done payment previously
                    // and to continue payment it the same curreny the input is disabled
                    // isDisabled={customPlanFields.currency.isDisabled}
                  />
                  {customPlanFields.currency.errMsg && (
                    <small className="text-danger">
                      {customPlanFields.currency.errMsg}
                    </small>
                  )}
                </FormGroup>
              </Col>
              <Col md="4">
                <FormGroup>
                  <label className="form-control-label">
                    Plan <strong style={{ color: "red" }}>*</strong>
                  </label>
                  <Select
                    options={
                      customPlanFields.planDuration.value === 1
                        ? planList.filter((p) => p.ID !== -1)
                        : planList.filter((p) => p.type !== "CUSTOM")
                    }
                    placeholder="Select Plan"
                    name="plan"
                    getOptionLabel={(options) => options.name}
                    getOptionValue={(options) => options.ID}
                    onChange={(selected, e) =>
                      handleSelectPlanChange(selected, e)
                    }
                    onBlur={() =>
                      handleOnBlur("plan", customPlanFields.plan.ID)
                    }
                    styles={CUSTOM_STYLES}
                    value={
                      planList?.find(
                        (option) => option.ID === customPlanFields.plan?.ID
                      ) || null
                    } // find the selected option based on value
                    isSearchable={true} // enable search functionality
                    className={
                      customPlanFields.plan.errMsg ? "is-invalid" : null
                    }
                  />
                  {customPlanFields.plan.errMsg && (
                    <small className="text-danger">
                      {customPlanFields.plan.errMsg}
                    </small>
                  )}
                </FormGroup>
              </Col>
            </Row>

            {/* {customPlanFields.planDuration.value !== 1 && ( */}
              <Row className="mt-3">
                {" "}
                <Col md="4">
                  {customPlanFields.plan.ID === -1 ? (
                    <FormGroup>
                      <label className="form-control-label">
                        Credits <strong style={{ color: "red" }}>*</strong>
                      </label>
                      <Input
                        placeholder="Enter Personalized Credits"
                        type="text"
                        name="customCredits"
                        value={customPlanFields.customCredits.value}
                        onChange={(e) => {
                          const numVal = e.target.value.replace(/[^\d.]/g, "");
                          handleCustomCreditsChange(e.target.name, numVal);
                        }}
                        onBlur={(e) =>
                          handleOnBlur(e.target.name, e.target.value)
                        }
                        className={
                          customPlanFields.customCredits.errMsg
                            ? "is-invalid"
                            : ""
                        }
                        maxLength={7}
                        autoComplete="off"
                        disabled={customPlanFields.plan.ID === 0}
                      />
                      {customPlanFields.customCredits.errMsg && (
                        <small className="text-danger">
                          {customPlanFields.customCredits.errMsg}
                        </small>
                      )}
                    </FormGroup>
                  ) : (
                    <FormGroup>
                      <label className="form-control-label">
                        Plan Multiplier{" "}
                        <strong style={{ color: "red" }}>*</strong>
                      </label>
                      <Select
                        options={PLAN_MULTIPLIER}
                        placeholder="Select Plan Multiplier"
                        name="planMultiplier"
                        // getOptionLabel={(options) => options.location}
                        // getOptionValue={(options) => options.ID}
                        onChange={(selected, e) =>
                          handlePlanMultiplierChange(selected, e)
                        }
                        onBlur={() =>
                          handleOnBlur(
                            "planMultiplier",
                            customPlanFields.planMultiplier.value
                          )
                        }
                        styles={CUSTOM_STYLES}
                        value={PLAN_MULTIPLIER?.find(
                          (options) =>
                            options.value ===
                            customPlanFields.planMultiplier.value
                        )} // find the selected option based on value
                        isSearchable={true} // enable search functionality
                        className={
                          customPlanFields.planMultiplier.errMsg
                            ? "is-invalid"
                            : null
                        }
                      />
                      {customPlanFields.planMultiplier.errMsg && (
                        <small className="text-danger">
                          {customPlanFields.planMultiplier.errMsg}
                        </small>
                      )}
                    </FormGroup>
                  )}
                </Col>
                <Col md="4">
                  <FormGroup>
                    <label className="form-control-label">
                      Discount
                      {/* <strong style={{ color: "red" }}>*</strong> */}
                    </label>
                    <InputGroup>
                      <Input
                        placeholder="Enter Discount"
                        type="text"
                        name="discount"
                        value={customPlanFields.discount.value}
                        onChange={(e) => {
                          const numVal = e.target.value.replace(/[^\d.]/g, "");
                          handleDiscountSubtotalChange(e.target.name, numVal);
                        }}
                        onBlur={(e) =>
                          handleOnBlur(e.target.name, e.target.value)
                        }
                        className={
                          customPlanFields.discount.errMsg ? "is-invalid" : ""
                        }
                        maxLength={4}
                        autoComplete="off"
                        disabled={customPlanFields.plan.ID === 0}
                      />
                      <InputGroupText
                        style={{
                          borderTopLeftRadius: 0,
                          borderBottomLeftRadius: 0,
                          border:
                            customPlanFields.discount.errMsg &&
                            "0.1rem solid #fb6340",
                          borderLeft:
                            customPlanFields.discount.errMsg && "none",
                        }}
                      >
                        <InputGroupAddon addonType="append">
                          <i className={"fa-solid fa-percent"} />
                        </InputGroupAddon>
                      </InputGroupText>
                    </InputGroup>

                    {customPlanFields.discount.errMsg && (
                      <small className="text-danger">
                        {customPlanFields.discount.errMsg}
                      </small>
                    )}
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <label className="form-control-label">
                      Sub Total <strong style={{ color: "red" }}>*</strong>
                      {customPlanFields.plan.ratePerInvite &&
                        `(${
                          customPlanFields.plan.ID === -1
                            ? convertCommaSeperateNumToString(
                                customPlanFields.customCredits.value
                              )
                            : `${
                                isUSD
                                  ? CURRENCY_SYMBOL.USD
                                  : CURRENCY_SYMBOL.INR
                              }${convertCommaSeperateNumToString(basePrice)}`
                        } x ${
                          customPlanFields.plan.ID === -1
                            ? `${
                                isUSD
                                  ? CURRENCY_SYMBOL.USD
                                  : CURRENCY_SYMBOL.INR
                              }${ratePerInvite}`
                            : customPlanFields.planMultiplier.value
                        }) = ${
                          isUSD ? CURRENCY_SYMBOL.USD : CURRENCY_SYMBOL.INR
                        }${priceBeforeDiscount} ${
                          customPlanFields.discount.value &&
                          `of ${customPlanFields.discount.value}%`
                        }`}
                    </label>
                    <InputGroup>
                      <InputGroupText
                        style={{
                          borderTopRightRadius: 0,
                          borderBottomRightRadius: 0,
                          border:
                            customPlanFields.subTotal.errMsg &&
                            "0.1rem solid #fb6340",
                          borderRight:
                            customPlanFields.subTotal.errMsg && "none",
                        }}
                      >
                        <InputGroupAddon addonType="append">
                          <i
                            // onClick={toggleApiKeyVisibility}
                            className={
                              customPlanFields.currency.value === CURRENCY.USD
                                ? "fa-solid fa-dollar-sign"
                                : "fa-solid fa-indian-rupee-sign"
                            }
                          />
                        </InputGroupAddon>
                      </InputGroupText>
                      <Input
                        placeholder="Enter Subtotal Price"
                        type="text"
                        name="subTotal"
                        value={customPlanFields.subTotal.value}
                        onChange={(e) => {
                          const numVal = e.target.value.replace(/[^\d.]/g, "");
                          handleDiscountSubtotalChange(e.target.name, numVal);
                        }}
                        onBlur={(e) =>
                          handleOnBlur(e.target.name, e.target.value)
                        }
                        className={
                          customPlanFields.subTotal.errMsg ? "is-invalid" : ""
                        }
                        maxLength={7}
                        autoComplete="off"
                        disabled={
                          customPlanFields.plan.ID === 0 || isNaN(basePrice)
                        }
                      />
                    </InputGroup>
                    {customPlanFields.subTotal.errMsg && (
                      <small className="text-danger">
                        {customPlanFields.subTotal.errMsg}
                      </small>
                    )}
                  </FormGroup>
                </Col>
              </Row>
            {/* )} */}

            {customPlanFields.paymentType !== "OFFLINE" && (
              <Row className="mt-3">
                <Col
                  md="4"
                  className={
                    customPlanFields.planDuration.value !== 1 ? "mt-3" : ""
                  }
                >
                  <FormGroup>
                    <label className="form-control-label">
                      Payment Link Expiry
                      <strong style={{ color: "red" }}> *</strong>
                    </label>
                    <DatePicker
                      placeholderText="Select Expiry Date"
                      showIcon
                      icon={
                        <i className="fa-solid fa-calendar-day fa-md mt-2" />
                      }
                      toggleCalendarOnIconClick
                      selected={customPlanFields.linkExpiryDate.value}
                      onChange={(date) => handleDateChange(date)}
                      // onCalendarClose={() =>
                      //   handleOnBlur(
                      //     false,
                      //     "startDate",
                      //     dateForDatePicker.startDate
                      //   )
                      // }
                      onBlur={(e) =>
                        handleOnBlur(
                          "linkExpiryDate",
                          customPlanFields.linkExpiryDate.value
                        )
                      }
                      dateFormat="dd MMM yyyy"
                      minDate={new Date()}
                      maxDate={new Date().setMonth(new Date().getMonth() + 1)}
                      className={`form-control ${
                        customPlanFields.linkExpiryDate.errMsg
                          ? "is-invalid"
                          : ""
                      }`}
                      style={styles.datePickerContainer}
                      wrapperClassName="form-control "
                      disabled={customPlanFields.isNoExpiryChecked}
                    />
                    {customPlanFields.linkExpiryDate.errMsg && (
                      <small className="text-danger">
                        {customPlanFields.linkExpiryDate.errMsg}
                      </small>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      <Checkbox
                        className="p-1"
                        size="small"
                        checked={customPlanFields.isNoExpiryChecked}
                        onChange={(e) => {
                          setCustomPlanFields((prevState) => {
                            return {
                              ...prevState,
                              linkExpiryDate: {
                                value: "",
                                errMsg: "",
                              },
                              isNoExpiryChecked: e.target.checked,
                            };
                          });
                        }}
                      />
                      <span className="text-sm">No Expiry</span>
                    </div>
                  </FormGroup>
                </Col>
              </Row>
            )}

            {customPlanFields.plan.ID !== 0 && !isNaN(basePrice) && (
              <Card className="m-0 mt-3">
                <CardBody>
                  <Col className="py-2">
                    {customPlanFields.plan.ID === -1 ? (
                      <>
                        <Row className="mb-2">
                          <Col xs={6} className="text-muted">
                            Personalized Credits Given{" "}
                          </Col>
                          <Col xs={1}>:</Col>
                          <Col xs={5} className="text-right fw-bold">
                            {convertCommaSeperateNumToString(
                              customPlanFields.customCredits.value
                            )}
                          </Col>
                        </Row>
                        <Row className="mb-2">
                          <Col xs={6} className="text-muted">
                            Rate Per Invite{" "}
                            {`(${customPlanFields.plan.planName})`}
                          </Col>
                          <Col xs={1}>:</Col>
                          <Col
                            xs={5}
                            className="text-right fw-bold text-primary"
                          >
                            ×{isUSD ? CURRENCY_SYMBOL.USD : CURRENCY_SYMBOL.INR}
                            {ratePerInvite}
                          </Col>
                        </Row>
                      </>
                    ) : (
                      <>
                        <Row className="mb-2">
                          <Col xs={6} className="text-muted">
                            Base Price{" "}
                          </Col>
                          <Col xs={1}>:</Col>
                          <Col xs={5} className="text-right fw-bold">
                            {isUSD ? CURRENCY_SYMBOL.USD : CURRENCY_SYMBOL.INR}
                            {convertCommaSeperateNumToString(basePrice, true)}
                          </Col>
                        </Row>
                        {customPlanFields.planMultiplier.value > 1 && (
                          <Row className="mb-2">
                            <Col xs={6} className="text-muted">
                              Multiplier
                            </Col>
                            <Col xs={1}>:</Col>
                            <Col
                              xs={5}
                              className="text-right fw-bold text-primary"
                            >
                              × {customPlanFields.planMultiplier.value}
                            </Col>
                          </Row>
                        )}
                      </>
                    )}
                    {customPlanFields.discount.value !== "" && (
                      <Row className="mb-2">
                        <Col xs={6} className="text-muted">
                          Discount ({customPlanFields.discount.value}
                          %)
                        </Col>
                        <Col xs={1}>:</Col>
                        <Col xs={5} className="text-right fw-bold text-danger">
                          -{isUSD ? CURRENCY_SYMBOL.USD : CURRENCY_SYMBOL.INR}
                          {convertCommaSeperateNumToString(discountGiven, true)}
                        </Col>
                      </Row>
                    )}

                    {customPlanFields.currency.value === CURRENCY.INR && (
                      <Row className="mb-2">
                        <Col xs={6} className="text-muted">
                          GST (18%)
                        </Col>
                        <Col xs={1}>:</Col>
                        <Col xs={5} className="text-right fw-bold">
                          +{CURRENCY_SYMBOL.INR}
                          {convertCommaSeperateNumToString(gstApplied, true)}
                        </Col>
                      </Row>
                    )}

                    <hr className="my-2" />

                    <Row className="py-2 rounded bg-light">
                      <Col xs={6} className="fw-bold text-dark">
                        Final Price
                      </Col>
                      <Col xs={1} className="fw-bold">
                        :
                      </Col>

                      <Col xs={5} className="text-right fw-bold text-dark">
                        {customPlanFields.currency.value === CURRENCY.USD
                          ? CURRENCY_SYMBOL.USD
                          : CURRENCY_SYMBOL.INR}
                        {customPlanFields.currency.value === CURRENCY.INR
                          ? convertCommaSeperateNumToString(
                              gstAddedFinalPrice,
                              true
                            )
                          : convertCommaSeperateNumToString(
                              customPlanFields.subTotal.value,
                              true
                            )}
                      </Col>
                    </Row>

                    <hr className="my-2" />

                    {customPlanFields.plan.ID !== -1 && (
                      <Row className="mb-2">
                        <Col xs={6} className="text-muted">
                          Credits Given
                          {/* to show multiplication calulation only when it is not edited */}
                          {!customPlanFields.creditsGiven.isUserEntered &&
                            ` (${customPlanFields.plan.credits} x ${customPlanFields.planMultiplier.value})`}
                        </Col>
                        <Col xs={1}>:</Col>
                        <Col xs={5} className="text-right fw-bold">
                          <div className="d-flex align-items-start justify-content-end">
                            {/* to show userEnteredValue when the value is edited by user otherwise the value set when selecting th plan */}
                            {showEditInput.credits ? (
                              <div className="d-flex flex-column w-100">
                                <Input
                                  placeholder="Enter Credits"
                                  type="text"
                                  name="creditsGiven"
                                  bsSize="sm"
                                  value={
                                    customPlanFields.creditsGiven
                                      .userEnteredValue
                                  }
                                  onChange={(e) => {
                                    const numVal = e.target.value.replace(
                                      /[^\d.]/g,
                                      ""
                                    );
                                    handleCreditsChange(e.target.name, numVal);
                                  }}
                                  onBlur={(e) =>
                                    handleOnBlur(e.target.name, e.target.value)
                                  }
                                  className={
                                    customPlanFields.creditsGiven.errMsg
                                      ? "is-invalid"
                                      : ""
                                  }
                                  maxLength={7}
                                  autoComplete="off"
                                />
                                {customPlanFields.creditsGiven.errMsg && (
                                  <small className="text-danger">
                                    {customPlanFields.creditsGiven.errMsg}
                                  </small>
                                )}
                              </div>
                            ) : customPlanFields.creditsGiven.isUserEntered ? (
                              convertCommaSeperateNumToString(
                                customPlanFields.creditsGiven.userEnteredValue
                              ) + " Credits"
                            ) : (
                              convertCommaSeperateNumToString(
                                customPlanFields.creditsGiven.value
                              ) + " Credits"
                            )}{" "}
                            {/* save icon */}
                            {showEditInput.credits && (
                              <i
                                className={
                                  "fa-solid fa-check ml-2 text-white bg-dark p-1 mt-1 rounded"
                                }
                                style={{
                                  opacity: customPlanFields.creditsGiven.errMsg
                                    ? 0.5
                                    : 1,
                                }}
                                onClick={() => {
                                  onSubmitCreditsGiven();
                                }}
                              />
                            )}
                            {/* edit and clear icon */}
                            <i
                              className={
                                showEditInput.credits
                                  ? "fa-solid fa-xmark ml-2 bg-light p-1 mt-1 rounded"
                                  : "fas fa-edit ml-2 text-info mt-1"
                              }
                              onClick={() => {
                                //in case of when clicked on clear input to clear
                                if (showEditInput.credits) {
                                  setCustomPlanFields((prevState) => {
                                    return {
                                      ...prevState,
                                      creditsGiven: {
                                        ...customPlanFields.creditsGiven,
                                        userEnteredValue:
                                          customPlanFields.creditsGiven.value,
                                        errMsg: "",
                                        isUserEntered: false,
                                      },
                                    };
                                  });
                                }
                                setShowEditInput({
                                  ...showEditInput,
                                  credits: !showEditInput.credits,
                                });
                              }}
                            />
                          </div>
                        </Col>
                      </Row>
                    )}
                    {/* showing carryforward for only the plans where it is non-zero */}
                    {customPlanFields.plan?.creditsCarryForward !== "0" && (
                      <Row>
                        <Col xs={6} className="text-muted">
                          Carry-Forward
                          {!customPlanFields.creditsCarryForward
                            .isUserEntered &&
                            ` (${customPlanFields.plan.creditsCarryForward} x ${customPlanFields.planMultiplier.value})`}
                        </Col>
                        <Col xs={1}>:</Col>
                        <Col xs={5} className="text-right fw-bold ">
                          <div className="d-flex align-items-start justify-content-end">
                            {showEditInput.carryForward ? (
                              <div className="d-flex flex-column w-100">
                                <Input
                                  placeholder="Enter Credits to be Carry Forward"
                                  type="text"
                                  name="creditsCarryForward"
                                  bsSize="sm"
                                  value={
                                    customPlanFields.creditsCarryForward
                                      .userEnteredValue
                                  }
                                  onChange={(e) => {
                                    const numVal = e.target.value.replace(
                                      /[^\d.]/g,
                                      ""
                                    );
                                    handleCreditsChange(e.target.name, numVal);
                                  }}
                                  onBlur={(e) =>
                                    handleOnBlur(e.target.name, e.target.value)
                                  }
                                  className={
                                    customPlanFields.creditsCarryForward.errMsg
                                      ? "is-invalid"
                                      : ""
                                  }
                                  maxLength={7}
                                  autoComplete="off"
                                />
                                {customPlanFields.creditsCarryForward
                                  .errMsg && (
                                  <small className="text-danger">
                                    {
                                      customPlanFields.creditsCarryForward
                                        .errMsg
                                    }
                                  </small>
                                )}
                              </div>
                            ) : customPlanFields.creditsCarryForward
                                .isUserEntered ? (
                              convertCommaSeperateNumToString(
                                customPlanFields.creditsCarryForward
                                  .userEnteredValue
                              ) + " Credits"
                            ) : (
                              convertCommaSeperateNumToString(
                                customPlanFields.creditsCarryForward.value
                              ) + " Credits"
                            )}{" "}
                            {showEditInput.carryForward && (
                              <i
                                className={
                                  "fa-solid fa-check ml-2 text-white bg-dark p-1 mt-1 rounded"
                                }
                                style={{
                                  opacity: customPlanFields.creditsCarryForward
                                    .errMsg
                                    ? 0.5
                                    : 1,
                                }}
                                onClick={() => {
                                  onSubmitCarryForward();
                                }}
                              />
                            )}
                            <i
                              className={
                                showEditInput.carryForward
                                  ? "fa-solid fa-xmark ml-2 bg-light p-1 mt-1 rounded"
                                  : "fas fa-edit ml-2 text-info mt-1"
                              }
                              onClick={() => {
                                //in case of when clicked on clear input to clear
                                if (showEditInput.carryForward) {
                                  setCustomPlanFields((prevState) => {
                                    return {
                                      ...prevState,
                                      creditsCarryForward: {
                                        ...prevState.creditsCarryForward,
                                        userEnteredValue:
                                          customPlanFields.creditsCarryForward
                                            .value,
                                        errMsg: "",
                                        isUserEntered: false,
                                      },
                                    };
                                  });
                                }
                                setShowEditInput({
                                  ...showEditInput,
                                  carryForward: !showEditInput.carryForward,
                                });
                              }}
                            />
                          </div>
                        </Col>
                      </Row>
                    )}
                  </Col>
                </CardBody>
              </Card>
            )}
          </ModalBody>
          <ModalFooter style={{ backgroundColor: "#f5f5f5" }}>
            <Button
              color="gray"
              onClick={() => {
                props.handleClose();
              }}
            >
              Cancel
            </Button>
            <Button
              color="info"
              type="submit"
              onClick={handleSubmit}
              disabled={
                requiredFields.some(
                  (key) =>
                    (typeof customPlanFields[key] === "object" &&
                      customPlanFields[key].errMsg !== "") ||
                    (typeof customPlanFields[key] === "string" &&
                      !customPlanFields[key])
                ) ||
                showEditInput.credits ||
                showEditInput.carryForward
              }
            >
              Create Plan
            </Button>
          </ModalFooter>
        </>
      </Modal>
    </>
  );
}

export default CreateCustomPlan;

const styles = {
  backBropStyle: { color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 },
  datePickerContainer: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
  },
};
